import { useCallback, useEffect, useRef } from "react";
import {
  useMutation,
  type MutateOptions,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import {
  assertAuthAttemptCurrent,
  beginAuthAttempt,
  cancelAuthAttempt,
  cancelPendingAuthAttempt,
  isAuthAttemptCurrent,
  tagAuthAttemptResult,
  type AuthAttempt,
} from "lib/auth-attempt";
import { AuthSessionChangedError } from "lib/auth-session-ownership";

type AttemptVariables<T> = { variables: T; attempt: AuthAttempt };

/** Keep one request's lifetime through Query's asynchronous callbacks and reset. */
export function useAuthAttemptMutation<
  TData,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  mutationFn: (variables: TVariables, attempt: AuthAttempt) => Promise<TData>,
  options: Omit<UseMutationOptions<TData, TError, TVariables, TContext>, "mutationFn"> = {},
  beginAttempt: () => AuthAttempt = beginAuthAttempt
): UseMutationResult<TData, TError, TVariables, TContext> {
  const ownedAttempt = useRef<AuthAttempt>();
  const mutation = useMutation<TData, TError, AttemptVariables<TVariables>, TContext>(
    async (request) => {
      if (!request) throw new AuthSessionChangedError();
      const { variables, attempt } = request;
      assertAuthAttemptCurrent(attempt);
      try {
        const result = await mutationFn(variables, attempt);
        assertAuthAttemptCurrent(attempt);
        return tagAuthAttemptResult(result, attempt);
      } catch (error) {
        assertAuthAttemptCurrent(attempt);
        throw error;
      }
    },
    {
      ...options,
      onMutate: (request) => request && options.onMutate?.(request.variables),
      onSuccess: (data, request, context) => {
        if (!request) throw new AuthSessionChangedError();
        assertAuthAttemptCurrent(request.attempt);
        return options.onSuccess?.(data, request.variables, context);
      },
      onError: (error, request, context) => {
        // Query reset can clear variables while a pending mutation settles.
        if (request && isAuthAttemptCurrent(request.attempt)) {
          return options.onError?.(error, request.variables, context);
        }
      },
      onSettled: (data, error, request, context) => {
        if (request && isAuthAttemptCurrent(request.attempt)) {
          return options.onSettled?.(data, error, request.variables, context);
        }
      },
    }
  );

  const prepare = useCallback(
    (variables: TVariables) => {
      cancelAuthAttempt(ownedAttempt.current);
      const attempt = beginAttempt();
      ownedAttempt.current = attempt;
      return { variables, attempt };
    },
    [beginAttempt]
  );

  const callbacks = useCallback(
    (
      request: AttemptVariables<TVariables>,
      supplied?: MutateOptions<TData, TError, TVariables, TContext>
    ) => ({
      onSuccess: (data: TData, _variables: AttemptVariables<TVariables>, context: TContext) => {
        if (isAuthAttemptCurrent(request.attempt))
          supplied?.onSuccess?.(data, request.variables, context);
      },
      onError: (
        error: TError,
        _variables: AttemptVariables<TVariables>,
        context: TContext | undefined
      ) => {
        if (isAuthAttemptCurrent(request.attempt))
          supplied?.onError?.(error, request.variables, context);
      },
      onSettled: (
        data: TData | undefined,
        error: TError | null,
        _variables: AttemptVariables<TVariables>,
        context: TContext | undefined
      ) => {
        if (isAuthAttemptCurrent(request.attempt))
          supplied?.onSettled?.(data, error, request.variables, context);
      },
    }),
    []
  );

  const mutate = useCallback(
    (variables: TVariables, supplied?: MutateOptions<TData, TError, TVariables, TContext>) => {
      const request = prepare(variables);
      mutation.mutate(request, callbacks(request, supplied));
    },
    [mutation.mutate, prepare, callbacks]
  );
  const mutateAsync = useCallback(
    (variables: TVariables, supplied?: MutateOptions<TData, TError, TVariables, TContext>) => {
      const request = prepare(variables);
      return mutation.mutateAsync(request, callbacks(request, supplied));
    },
    [mutation.mutateAsync, prepare, callbacks]
  );
  const reset = useCallback(() => {
    cancelAuthAttempt(ownedAttempt.current);
    mutation.reset();
  }, [mutation.reset]);

  useEffect(
    () => () => {
      cancelPendingAuthAttempt(ownedAttempt.current);
    },
    []
  );

  return { ...mutation, variables: mutation.variables?.variables, mutate, mutateAsync, reset };
}
